import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api-client.js';

const STATUS_FLOW = ['lead', 'negotiating', 'pending', 'finance_pending', 'ready_to_sign', 'signed', 'funded', 'delivered', 'completed'];
const STATUS_LABELS: Record<string, string> = {
  lead: 'Lead', negotiating: 'Negotiating', pending: 'Pending',
  finance_pending: 'Finance', ready_to_sign: 'Ready to Sign',
  signed: 'Signed', funded: 'Funded', delivered: 'Delivered',
  completed: 'Completed', lost: 'Lost', cancelled: 'Cancelled',
};

function fmt(cents: number | null | undefined): string {
  if (cents == null) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

// ── Deal Detail + Flow ────────────────────────────
export default function DealFlowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showCalc, setShowCalc] = useState(false);
  const [calcState, setCalcState] = useState('TX');

  const { data } = useQuery({
    queryKey: ['deal', id],
    queryFn: () => api.get<any>(`/deals/${id}`),
    enabled: !!id,
  });
  const deal = data?.data;

  const updateDeal = useMutation({
    mutationFn: (updates: Record<string, unknown>) => api.patch(`/deals/${id}`, updates),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deal', id] }),
  });

  const calculateDeal = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/deals/${id}/calculate`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deal', id] }),
  });

  const generateDocs = useMutation({
    mutationFn: () => api.post(`/deals/${id}/generate-docs`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deal', id] }),
  });

  if (!deal) return <div className="page text-center py-12 text-surface-500">Loading deal...</div>;

  const currentIdx = STATUS_FLOW.indexOf(deal.status);
  const nextStatus = currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[currentIdx + 1] : null;

  return (
    <div className="page">
      <button onClick={() => navigate('/deals')} className="text-brand-400 text-sm mb-4">&larr; Back to Deals</button>

      {/* Header */}
      <div className="card p-6 mb-4">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold">{deal.year} {deal.make} {deal.model}</h2>
            <p className="text-surface-500 text-sm">{deal.deal_number} | VIN: {deal.vin}</p>
          </div>
          <span className={`text-sm px-3 py-1 rounded-full font-medium ${
            deal.status === 'completed' || deal.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
            deal.status === 'lost' || deal.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
            'bg-brand-500/20 text-brand-400'
          }`}>{STATUS_LABELS[deal.status] ?? deal.status}</span>
        </div>
      </div>

      {/* Status Pipeline */}
      <div className="mb-4 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {STATUS_FLOW.map((s, i) => (
            <div key={s} className={`text-xs px-3 py-1.5 rounded ${
              i < currentIdx ? 'bg-green-500/20 text-green-400' :
              i === currentIdx ? 'bg-brand-500 text-white' :
              'bg-surface-800 text-surface-500'
            }`}>{STATUS_LABELS[s]}</div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {nextStatus && (
          <button
            onClick={() => updateDeal.mutate({ status: nextStatus })}
            className="btn-primary text-sm px-4 py-2"
          >Advance to {STATUS_LABELS[nextStatus]}</button>
        )}
        <button onClick={() => setShowCalc(!showCalc)} className="btn-secondary text-sm px-4 py-2">
          {showCalc ? 'Hide' : 'Show'} Calculator
        </button>
        <button onClick={() => generateDocs.mutate()} className="btn-secondary text-sm px-4 py-2">
          {generateDocs.isPending ? 'Generating...' : 'Generate Docs'}
        </button>
        {deal.status !== 'lost' && deal.status !== 'cancelled' && deal.status !== 'completed' && (
          <button
            onClick={() => updateDeal.mutate({ status: 'lost', lost_reason: 'Customer walked' })}
            className="text-sm px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20"
          >Mark Lost</button>
        )}
      </div>

      {/* Calculator */}
      {showCalc && (
        <div className="card p-4 mb-4 border border-brand-500/30">
          <h3 className="font-semibold mb-3">Deal Calculator</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-surface-500">State</label>
              <select value={calcState} onChange={(e) => setCalcState(e.target.value)} className="input">
                {['TX', 'CA', 'FL', 'NY', 'OH'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <button
            onClick={() => calculateDeal.mutate({ state: calcState })}
            className="btn-primary text-sm px-6 py-2 mt-3"
          >{calculateDeal.isPending ? 'Calculating...' : 'Calculate'}</button>
        </div>
      )}

      {/* Deal Financials */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div className="card p-4">
          <h3 className="font-semibold mb-3">Pricing</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-surface-500">Sale Price</span><span className="font-medium">{fmt(deal.sale_price_cents)}</span></div>
            {deal.trade_allowance_cents > 0 && (
              <>
                <div className="flex justify-between"><span className="text-surface-500">Trade Allowance</span><span className="text-green-400">-{fmt(deal.trade_allowance_cents)}</span></div>
                {deal.trade_payoff_cents > 0 && <div className="flex justify-between"><span className="text-surface-500">Trade Payoff</span><span className="text-red-400">+{fmt(deal.trade_payoff_cents)}</span></div>}
              </>
            )}
            {deal.tax_cents > 0 && <div className="flex justify-between"><span className="text-surface-500">Tax ({((deal.tax_rate ?? 0) * 100).toFixed(2)}%)</span><span>{fmt(deal.tax_cents)}</span></div>}
            {deal.total_fees_cents > 0 && <div className="flex justify-between"><span className="text-surface-500">Fees</span><span>{fmt(deal.total_fees_cents)}</span></div>}
            <div className="flex justify-between border-t border-surface-700 pt-2 font-bold text-base">
              <span>Total Due</span><span>{fmt(deal.total_due_cents)}</span>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <h3 className="font-semibold mb-3">Finance</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-surface-500">Payment Type</span><span className="capitalize">{deal.payment_type ?? 'Cash'}</span></div>
            {deal.down_payment_cents > 0 && <div className="flex justify-between"><span className="text-surface-500">Down Payment</span><span>{fmt(deal.down_payment_cents)}</span></div>}
            {deal.finance_amount_cents > 0 && (
              <>
                <div className="flex justify-between"><span className="text-surface-500">Financed</span><span>{fmt(deal.finance_amount_cents)}</span></div>
                <div className="flex justify-between"><span className="text-surface-500">APR</span><span>{((deal.apr ?? 0) * 100).toFixed(1)}%</span></div>
                <div className="flex justify-between"><span className="text-surface-500">Term</span><span>{deal.term_months} mo</span></div>
                <div className="flex justify-between border-t border-surface-700 pt-2 font-bold">
                  <span>Monthly</span><span className="text-brand-400">{fmt(deal.monthly_payment_cents)}/mo</span>
                </div>
              </>
            )}
            {deal.front_gross_cents != null && (
              <div className="flex justify-between pt-2 border-t border-surface-700">
                <span className="text-surface-500">Front Gross</span>
                <span className={deal.front_gross_cents >= 0 ? 'text-green-400' : 'text-red-400'}>{fmt(deal.front_gross_cents)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customer & Trade Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div className="card p-4">
          <h3 className="font-semibold mb-2">Customer</h3>
          <p className="font-medium">{deal.customer_name}</p>
          {deal.customer_phone && <p className="text-sm text-surface-500">{deal.customer_phone}</p>}
          {deal.customer_email && <p className="text-sm text-surface-500">{deal.customer_email}</p>}
        </div>

        {deal.trade_vin && (
          <div className="card p-4">
            <h3 className="font-semibold mb-2">Trade-In</h3>
            <p className="font-medium">{deal.trade_year} {deal.trade_make} {deal.trade_model}</p>
            <p className="text-sm text-surface-500">VIN: {deal.trade_vin}</p>
            {deal.trade_mileage && <p className="text-sm text-surface-500">{deal.trade_mileage.toLocaleString()} mi</p>}
            <div className="flex justify-between mt-2 text-sm">
              <span className="text-surface-500">Allowance</span><span className="text-green-400">{fmt(deal.trade_allowance_cents)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Documents */}
      {deal.documents?.length > 0 && (
        <div className="card p-4">
          <h3 className="font-semibold mb-2">Documents</h3>
          <div className="space-y-2">
            {deal.documents.map((doc: any) => (
              <div key={doc.id} className="flex justify-between items-center text-sm py-1">
                <span>{doc.title}</span>
                <div className="flex gap-2 items-center">
                  {doc.is_signed && <span className="text-xs text-green-400">Signed</span>}
                  {doc.url && (
                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">
                      View
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
